// src/auth/authConfig.js - React Integration with OIDC Package
import SecureOIDCAxiosInterceptor from "@reliance/oidc-axios-interceptor"; // Your package
import axios from "axios";
import { useMemo, useState, useEffect, useCallback } from "react"; // React imports HERE, not in package

import { envVariables } from "../../config/appConfig";

// Create custom axios instance for API calls
export const apiClient = axios.create({
  baseURL: envVariables.API_BASE_URL + "/v3",
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
});

apiClient.interceptors.response.use(
  (response) => response.data, // success → return only data
  (error) => Promise.reject(error), // forward error
);

const disableAutoRefreshExact = [
  "/", // landing page
  "/login", // your app login route
  "/logout", // logout placeholder
  "/reset-password", // custom reset UI
];

const disableAutoRefreshPrefix = [
  "/callback", // OIDC callback
  "/reset", // Keycloak reset actions like /reset?kc_action=UPDATE_PASSWORD
];

function shouldAutoRefresh(pathname) {
  // exact match routes
  if (disableAutoRefreshExact.includes(pathname)) {
    return false;
  }

  // prefix match routes
  if (disableAutoRefreshPrefix.some((prefix) => pathname.startsWith(prefix))) {
    return false;
  }

  return true;
}

const OIDC_DISCOVERY_KEY = "oidc_discovery_config";
const OIDC_PKCE_STATE_KEY = "oidc_pkce_state";
const OIDC_CONFIG_FINGERPRINT_KEY = "oidc_config_fingerprint";
// The package only persists access/refresh tokens, so we track the ID token
// ourselves — Keycloak's end_session_endpoint needs it as `id_token_hint` to
// silently invalidate the SSO session; without it, Keycloak shows a manual
// "Do you want to log out?" confirmation page instead.
const OIDC_ID_TOKEN_KEY = "oidc_id_token";

// Discovery config, tokens, etc. are cached in localStorage keyed by name only,
// so switching OIDC_ISSUER/OIDC_CLIENT_ID (e.g. between environments) without a
// full storage clear leaves stale values from the previous config behind.
// Wipe anything OIDC-related when the configured issuer/client no longer
// matches what was last cached.
function clearStaleOIDCCache() {
  const fingerprint = JSON.stringify({
    issuer: envVariables.OIDC_ISSUER,
    clientId: envVariables.OIDC_CLIENT_ID,
  });
  const previousFingerprint = localStorage.getItem(OIDC_CONFIG_FINGERPRINT_KEY);

  // No stored fingerprint (e.g. first run after adding this check) still counts
  // as a mismatch — there may already be stale cache from before this existed.
  if (previousFingerprint !== fingerprint) {
    [
      envVariables.OIDC_TOKEN_NAME,
      envVariables.OIDC_REFRESH_TOKEN_NAME,
      `${envVariables.OIDC_TOKEN_NAME}_metadata`,
      OIDC_DISCOVERY_KEY,
      OIDC_PKCE_STATE_KEY,
      OIDC_ID_TOKEN_KEY,
      "isAuthenticated",
    ].forEach((key) => localStorage.removeItem(key));
  }

  localStorage.setItem(OIDC_CONFIG_FINGERPRINT_KEY, fingerprint);
}

clearStaleOIDCCache();

// Initialize OIDC interceptor (framework-agnostic)
export const auth = new SecureOIDCAxiosInterceptor({
  // OIDC Configuration
  issuer: envVariables.OIDC_ISSUER,
  clientId: envVariables.OIDC_CLIENT_ID,
  redirectUri: `${window.location.origin}/callback`,
  scope: "openid profile email roles",
  authorization_endpoint: `${envVariables.OIDC_ISSUER}/protocol/openid-connect/auth`,
  token_endpoint: `${envVariables.OIDC_ISSUER}/protocol/openid-connect/token`,
  endSession_endpoint: `${envVariables.OIDC_ISSUER}/protocol/openid-connect/logout`,
  discoveryUrl: `${envVariables.OIDC_ISSUER}/.well-known/openid-configuration`,
  // Storage configuration
  storage: envVariables.OIDC_STORAGE,

  // Token management
  autoRefresh:
    shouldAutoRefresh(window.location.pathname) &&
    localStorage.getItem("isAuthenticated") === "true",
  refreshThreshold: envVariables.OIDC_REFRESH_THRESHOLD,
  tokenKey: envVariables.OIDC_TOKEN_NAME,
  refreshTokenKey: envVariables.OIDC_REFRESH_TOKEN_NAME,
  discoveryKey: OIDC_DISCOVERY_KEY,
  pkceStateKey: OIDC_PKCE_STATE_KEY,

  // Use our custom axios instance
  axiosInstance: apiClient,

  // Callbacks
  onTokenRefresh: (tokenData) => {
    // Optional: notify user or update UI
  },

  onAuthError: (error) => {
    // Handle different error types
    switch (error.name) {
      case "DiscoveryError":
        console.error("Failed to discover OIDC configuration");
        break;
      case "TokenExpiredError":
        // The package only reaches clearTokens() via refreshToken()'s own
        // catch block, so an error thrown before that (e.g. no refresh
        // token stored) leaves the stale access token behind — clear it
        // ourselves before redirecting.
        auth.tokenManager.clearTokens();
        window.location.href = "/login?reason=expired";
        break;
      case "RefreshFailedError":
        auth.tokenManager.clearTokens();
        window.location.href = "/login?reason=refresh_failed";
        break;
      default:
        auth.tokenManager.clearTokens();
        window.location.href = "/login?reason=auth_error";
    }
  },

  onDiscoveryComplete: (config) => {
    // console.log("Server capabilities:", {
    //   supportsPKCE: config.code_challenge_methods_supported?.includes("S256"),
    //   supportsRefresh: config.grant_types_supported?.includes("refresh_token"),
    //   supportedScopes: config.scopes_supported,
    // });
  },
});

// The interceptor already retries a 401 once after a token refresh (see
// setupInterceptors in the oidc-axios-interceptor package). A 401 that
// survives that retry means the backend is rejecting an otherwise-valid
// Keycloak token (e.g. deactivated account, revoked session) rather than
// the token being merely expired, so the package's own onAuthError never
// fires for it — callers need to force the logout themselves.
export function forceLogout(reason = "auth_error") {
  console.warn(`Forcing logout: ${reason}`);

  const idToken = localStorage.getItem(OIDC_ID_TOKEN_KEY);
  const endSessionEndpoint =
    auth.getDiscoveryConfig()?.end_session_endpoint ||
    `${envVariables.OIDC_ISSUER}/protocol/openid-connect/logout`;
  const redirectUri = `${window.location.origin}/`;

  auth.tokenManager.clearTokens();
  localStorage.removeItem("isAuthenticated");
  localStorage.removeItem(OIDC_ID_TOKEN_KEY);

  // Built by hand instead of calling auth.logout(redirectUri): the package
  // only ever sends client_id + post_logout_redirect_uri, and without an
  // id_token_hint Keycloak can't verify which session it's ending, so it
  // shows a manual "Do you want to log out?" confirmation page instead of
  // silently invalidating the SSO session and redirecting back to us.
  const params = new URLSearchParams({ post_logout_redirect_uri: redirectUri });
  params.set("client_id", envVariables.OIDC_CLIENT_ID);
  if (idToken) {
    params.set("id_token_hint", idToken);
  }

  window.location.href = `${endSessionEndpoint}?${params.toString()}`;
}

// ========================================================================
// REACT HOOK - Moved from the OIDC package to keep package framework-agnostic
// ========================================================================

/**
 * Custom React hook for OIDC authentication
 * This provides React-specific state management on top of the framework-agnostic OIDC package
 */
export function useOIDCAuth() {
  // React state - this is why it's in the React app, not the package
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [discoveryConfig, setDiscoveryConfig] = useState(null);
  const [error, setError] = useState(null);

  // Initialize the OIDC client
  useEffect(() => {
    let isMounted = true;
    const initialize = async () => {
      try {
        await auth.initialize();
        setIsInitialized(true);
        setIsAuthenticated(auth.isAuthenticated());
        setDiscoveryConfig(auth.getDiscoveryConfig());
        setError(null);
      } catch (initError) {
        setError(initError);
        setIsInitialized(true); // Set to true even on error so UI can handle it
      }
    };

    initialize();
    return () => {
      isMounted = false;
    };
  }, []); // Empty dependency array - only run once

  // Periodically check auth status
  useEffect(() => {
    if (!isInitialized) return;

    const checkAuth = () => {
      const currentAuthState = auth.isAuthenticated();
      setIsAuthenticated(currentAuthState);

      if (currentAuthState) {
        localStorage.setItem("isAuthenticated", "true");
      } else {
        localStorage.removeItem("isAuthenticated");
      }
    };

    // Check immediately
    checkAuth();

    // Check every 4 minutes
    const interval = setInterval(checkAuth, 4 * 60000);

    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        checkAuth();
      }
    };

    const handleFocus = () => {
      checkAuth();
    };

    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("focus", handleFocus);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("focus", handleFocus);
    };
  }, [isInitialized]);

  // Login function with React error handling
  const login = useCallback(
    async (additionalParams = {}) => {
      if (!isInitialized) {
        const error = new Error("OIDC client not initialized");
        setError(error);
        throw error;
      }

      try {
        setError(null);
        const result = await auth.initiateLogin(additionalParams);
        return result;
      } catch (loginError) {
        console.error("Login initiation failed:", loginError);
        setError(loginError);
        // throw loginError;
      }
    },
    [isInitialized],
  );

  // Handle OAuth callback with React state updates
  const handleCallback = useCallback(async (callbackUrl) => {
    try {
      setError(null);
      const tokenData = await auth.handleCallback(callbackUrl);
      // The package doesn't persist id_token itself (only access/refresh) —
      // stash it ourselves so forceLogout() can pass it as id_token_hint.
      if (tokenData?.id_token) {
        localStorage.setItem(OIDC_ID_TOKEN_KEY, tokenData.id_token);
      }
      setIsAuthenticated(true);
    } catch (callbackError) {
      console.error("❌ Callback handling failed:", callbackError);
      setError(callbackError);
      throw callbackError;
    }
  }, []);

  // Logout with React state cleanup
  const logout = useCallback((redirectUri) => {
    auth.logout(redirectUri);
    setIsAuthenticated(false);
    setError(null);
  }, []);

  const destroy = useCallback(() => {
    auth.destroy();
    setIsAuthenticated(false);
    setIsInitialized(false);
    setDiscoveryConfig(null);
    setError(null);
  }, []);

  // Get user info with React error handling
  const getUserInfo = useCallback(async () => {
    if (!isAuthenticated) {
      const error = new Error("User not authenticated");
      setError(error);
      throw error;
    }

    try {
      const userInfo = await auth.getUserInfo();
      return userInfo;
    } catch (userInfoError) {
      console.error("Failed to fetch user info:", userInfoError);
      setError(userInfoError);
      throw userInfoError;
    }
  }, [isAuthenticated]);

  // Utility functions
  const getAccessToken = useCallback(() => {
    return auth.getAccessToken();
  }, []);

  const getTokenPayload = useCallback(() => {
    return auth.getTokenPayload();
  }, []);

  // Clear error function
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Return all the state and functions that React components need
  return {
    // State
    isAuthenticated,
    isInitialized,
    discoveryConfig,
    error,

    // Methods
    login,
    handleCallback,
    logout,
    getUserInfo,
    getAccessToken,
    getTokenPayload,
    clearError,
    destroy,
    refreshToken: () => auth.refreshToken(), // Expose refreshToken directly

    // Computed values
    user: auth.getTokenPayload(),

    // Auth instance (for advanced usage)
    auth,
  };
}

// ========================================================================
// ADDITIONAL HELPER HOOKS (React-specific)
// ========================================================================

/**
 * Hook to get token expiration information with React state
 */
export function useTokenStatus() {
  const [tokenInfo, setTokenInfo] = useState(null);

  useEffect(() => {
    const updateTokenInfo = () => {
      if (auth.tokenManager) {
        const info = auth.tokenManager.getTokenExpirationInfo();
        setTokenInfo(info);
      }
    };

    // Update immediately
    updateTokenInfo();

    // Update every 30 seconds
    const interval = setInterval(updateTokenInfo, 30000);

    return () => clearInterval(interval);
  }, []);

  return tokenInfo;
}

/**
 * Hook to check if user has specific roles
 */
export function useUserRoles(requiredRoles = []) {
  const { user } = useOIDCAuth();

  return useMemo(() => {
    if (!user || !user.roles) {
      return { hasAllRoles: false, hasAnyRole: false, userRoles: [] };
    }

    const userRoles = Array.isArray(user.roles) ? user.roles : [user.roles];
    const hasAllRoles = requiredRoles.every((role) => userRoles.includes(role));
    const hasAnyRole = requiredRoles.some((role) => userRoles.includes(role));

    return { hasAllRoles, hasAnyRole, userRoles };
  }, [user, requiredRoles]);
}

/**
 * Hook to check if user has specific scopes
 */
export function useUserScopes(requiredScopes = []) {
  const { user } = useOIDCAuth();

  return useMemo(() => {
    if (!user || !user.scope) {
      return { hasAllScopes: false, hasAnyScope: false, userScopes: [] };
    }

    const userScopes = user.scope.split(" ");
    const hasAllScopes = requiredScopes.every((scope) =>
      userScopes.includes(scope),
    );
    const hasAnyScope = requiredScopes.some((scope) =>
      userScopes.includes(scope),
    );

    return { hasAllScopes, hasAnyScope, userScopes };
  }, [user, requiredScopes]);
}
